/**
 * The room's YouTube player, kept on the position the server says it is at.
 *
 * The server never sees a frame of video. It says "this video, this many seconds
 * in, playing", and this puts the browser's own player there and keeps it there:
 * a video that has drifted is seeked back, one that was paused for everybody is
 * paused here too, and one that has been changed is loaded. A jukebox is a lot of
 * separate players agreeing on where they are, and agreeing is all this does.
 *
 * The position is measured from when the snapshot *arrived* rather than from the
 * timestamp on it, so the two clocks involved never have to be the same one. A
 * browser whose clock is a minute out plays in time with everyone else.
 *
 * This is the one part of the client that fetches from somewhere else: the frame
 * comes from YouTube and so does the script that drives it. Everything else here
 * is served by this server, and if youtube.com cannot be reached the jukebox is
 * the only thing that stops working.
 */

const API_SRC = 'https://www.youtube.com/iframe_api';

/** Seconds out of step before a player is seeked rather than left alone. */
const DRIFT_LIMIT = 2.5;

/** How often each player checks itself against the position it was given. */
const TICK_MS = 1000;

/** How long to wait for a play to take, before treating it as refused. */
const AUTOPLAY_GRACE_MS = 1500;

/** YT.PlayerState, spelled out so the numbers below read as something. */
const ENDED = 0;
const PLAYING = 1;
const BUFFERING = 3;

/**
 * Error codes worth giving up on a video for: malformed id, embedding refused,
 * and not found. 2 and 5 are "this browser cannot do it", which is a reason to
 * stop trying here but not a reason to take the track away from the room - see
 * how the server counts these reports in src/services/jukebox.js.
 */
const FATAL_ERRORS = new Set([100, 101, 150]);

let apiPromise = null;

/**
 * Loads YouTube's iframe API once per page.
 *
 * The script announces itself by calling a global, so an existing one is chained
 * rather than replaced: this is a page that may already have loaded it, and the
 * API does not offer a second way to be told it is ready.
 */
function loadApi() {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    if (globalThis.YT?.Player) {
      resolve(globalThis.YT);
      return;
    }
    const previous = globalThis.onYouTubeIframeAPIReady;
    globalThis.onYouTubeIframeAPIReady = () => {
      try {
        previous?.();
      } catch {
        /* not ours to care about */
      }
      resolve(globalThis.YT);
    };
    const script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    script.onerror = () => {
      apiPromise = null; // let a later attempt try again
      reject(new Error('Could not load the YouTube player.'));
    };
    document.head.append(script);
  });
  return apiPromise;
}

export class JukeboxPlayer {
  /**
   * `onEnded`, `onDuration` and `onFailed` are what this player learns that the
   * server cannot: they are reported back so the queue can move on.
   * `onBlocked(reason)` is called with 'sound' or null as the browser refuses or
   * allows audible playback.
   */
  constructor({ onEnded, onDuration, onFailed, onBlocked, volume = 1, muted = false } = {}) {
    this.onEnded = onEnded ?? (() => {});
    this.onDuration = onDuration ?? (() => {});
    this.onFailed = onFailed ?? (() => {});
    this.onBlocked = onBlocked ?? (() => {});

    this.player = null;
    this.ready = false;
    this.destroyed = false;
    this.volume = volume;
    this.muted = muted === true;
    /** The last snapshot to follow: { key, videoId, position, playing, at }. */
    this.target = null;
    /** What the player currently holds, so a re-load is only for a real change. */
    this.loadedVideoId = null;
    /** The track a duration has already been reported for. */
    this.reportedKey = null;
    this.blocked = null;
    this.ticker = null;
    this.graceTimer = null;
    this.sizeObserver = null;
    this.pendingResize = null;
  }

  /**
   * Builds the player inside `container`. The API replaces the element it is
   * given, so it gets a throwaway child rather than the container itself.
   */
  async mount(container) {
    const YT = await loadApi();
    if (this.destroyed) return;

    const seat = document.createElement('div');
    container.replaceChildren(seat);

    await new Promise((resolve) => {
      this.player = new YT.Player(seat, {
        width: '100%',
        height: '100%',
        playerVars: {
          controls: 0, // the room's controls are the room's, not each listener's
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          iv_load_policy: 3,
          origin: location.origin,
        },
        events: {
          onReady: () => {
            this.ready = true;
            this.applyVolume();
            this.apply();
            resolve();
          },
          onStateChange: (event) => this.onState(event.data),
          onError: (event) => this.onError(event.data),
        },
      });
    });

    this.ticker = setInterval(() => this.apply(), TICK_MS);
    // The container, not the iframe: the API replaced the iframe's element once
    // already and sizes it itself, while this box is the one the stylesheet
    // sizes and therefore the one that says how big the picture should be.
    this.watchSize(container);
  }

  /**
   * Keeps the picture filling its frame as the frame changes size.
   *
   * The iframe is sized by the stylesheet and follows along by itself, but what
   * is *inside* it is a page on another origin laying itself out to a viewport
   * it believes it has. It notices the window resizing and nothing else - so
   * dragging the bar between the jukebox and the transcript leaves the video
   * drawn at its old size inside a frame that has already moved on. `setSize`
   * is how the API says "look again", and this says it whenever the frame
   * changes for any reason: the drag, the sidebar, a room with cameras in it.
   */
  watchSize(box) {
    if (typeof ResizeObserver !== 'function') return;
    this.sizeObserver = new ResizeObserver(() => {
      if (this.pendingResize) cancelAnimationFrame(this.pendingResize);
      // One call per frame: a drag fires this on every pointer move.
      this.pendingResize = requestAnimationFrame(() => {
        this.pendingResize = null;
        if (!this.ready || this.destroyed) return;
        const width = Math.round(box.clientWidth);
        const height = Math.round(box.clientHeight);
        if (width > 0 && height > 0) this.safely(() => this.player.setSize(width, height));
      });
    });
    this.sizeObserver.observe(box);
  }

  /**
   * Adopts a server snapshot as the position to keep the player at.
   *
   * The moment measured from is `receivedAt`, stamped on the snapshot when it
   * arrived rather than read off the clock here. Calling this again with the
   * same snapshot therefore says the same thing - and it is called again on
   * every re-draw, which taking the time here would turn into a player that
   * never advances past the position it was last told about.
   */
  follow(snapshot) {
    const current = snapshot?.open ? snapshot.current : null;
    this.target = current
      ? {
          key: current.key,
          videoId: current.videoId,
          position: Number(snapshot.position) || 0,
          playing: snapshot.playing === true,
          at: snapshot.receivedAt ?? Date.now(),
        }
      : null;
    this.apply();
  }

  /** Where the track should be by now, in seconds. */
  expected() {
    if (!this.target) return 0;
    if (!this.target.playing) return this.target.position;
    return this.target.position + (Date.now() - this.target.at) / 1000;
  }

  /**
   * Brings the player into line with the target: the right video, at the right
   * point, playing or not. Called on every change and once a second besides,
   * since a player can drift or stall without telling anyone.
   */
  apply() {
    if (!this.ready || !this.player || this.destroyed) return;
    const target = this.target;

    if (!target) {
      if (this.loadedVideoId) {
        this.safely(() => this.player.stopVideo());
        this.loadedVideoId = null;
        this.setBlocked(null);
      }
      return;
    }

    const at = this.expected();
    if (target.videoId !== this.loadedVideoId) {
      this.loadedVideoId = target.videoId;
      this.reportedKey = null;
      this.setBlocked(null);
      this.safely(() => {
        const args = { videoId: target.videoId, startSeconds: at };
        if (target.playing) this.player.loadVideoById(args);
        else this.player.cueVideoById(args);
      });
      if (target.playing) this.watchForRefusal();
      return;
    }

    const state = this.safely(() => this.player.getPlayerState());
    const now = this.safely(() => this.player.getCurrentTime()) ?? 0;

    if (target.playing) {
      if (Math.abs(at - now) > DRIFT_LIMIT) this.safely(() => this.player.seekTo(at, true));
      if (state !== PLAYING && state !== BUFFERING) {
        this.safely(() => this.player.playVideo());
        this.watchForRefusal();
      }
    } else if (state === PLAYING || state === BUFFERING) {
      this.safely(() => this.player.pauseVideo());
      if (Math.abs(at - now) > DRIFT_LIMIT) this.safely(() => this.player.seekTo(at, true));
    }
  }

  onState(state) {
    if (state === PLAYING) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
      this.reportDuration();
    }
    // The end is reported rather than acted on: whether the room moves to the
    // next track is the server's to decide, and it hears this from everyone.
    if (state === ENDED && this.target?.playing) this.onEnded(this.target.key);
  }

  onError(code) {
    const key = this.target?.key;
    if (!key) return;
    if (FATAL_ERRORS.has(code)) this.onFailed(key, code);
    // A player that has errored will not recover on its own; the next snapshot
    // reloads it from scratch.
    this.loadedVideoId = null;
  }

  /** Tells the room how long the track is, once, from whoever loaded it first. */
  reportDuration() {
    const key = this.target?.key;
    if (!key || this.reportedKey === key) return;
    const duration = this.safely(() => this.player.getDuration());
    if (!duration || duration <= 0) return; // a live stream, or not loaded yet
    this.reportedKey = key;
    this.onDuration(key, duration);
  }

  /**
   * Browsers refuse to start audible playback that nobody asked for, so a
   * listener arriving mid-song can be handed a player that quietly does nothing.
   * When a play does not take, the picture is started muted - which is always
   * allowed - and the room is told to offer the sound back. Same bargain the
   * camera tiles strike in client/main.js, and it is struck the same way so
   * there is only one thing for a reader to learn.
   */
  watchForRefusal() {
    clearTimeout(this.graceTimer);
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      if (this.destroyed || !this.target?.playing) return;
      const state = this.safely(() => this.player.getPlayerState());
      if (state === PLAYING || state === BUFFERING) return;
      this.safely(() => {
        this.player.mute();
        this.player.playVideo();
      });
      this.setBlocked('sound');
    }, AUTOPLAY_GRACE_MS);
  }

  /** Gives the sound back after the listener has touched the page. */
  unblock() {
    if (!this.blocked) return;
    this.setBlocked(null);
    this.applyVolume(); // unmutes, unless the listener has it turned down anyway
    this.safely(() => this.player.playVideo());
  }

  setBlocked(reason) {
    if (this.blocked === reason) return;
    this.blocked = reason;
    this.onBlocked(reason);
  }

  /** `level` is 0..1, as everywhere else in the client; YouTube counts to 100. */
  setVolume(level, muted = false) {
    this.volume = Math.min(1, Math.max(0, Number(level) || 0));
    this.muted = muted === true;
    this.applyVolume();
  }

  /**
   * Silence is the mute switch, not a level of nought.
   *
   * `setVolume(0)` alone does not reliably silence this player: YouTube keeps
   * mute and level as two separate things, and `unMute()` restores the level
   * from before the mute rather than leaving the one just set - so a slider
   * dragged to the bottom would spring back up as soon as anything re-applied
   * the volume. Muting at zero says the same thing in the terms the player
   * actually keeps, and it is the only half of this that works on a phone at
   * all, where the level is the hardware's to decide. See canSetVolume().
   */
  applyVolume() {
    if (!this.ready) return;
    this.safely(() => {
      // Never rounded down to nothing: the level arriving here is the jukebox's
      // own times the master's, which can fall under YouTube's quietest step of
      // 1 while neither is muted - and a slider that is up but silent reads as
      // broken. Silence is the mute switch's job, just below.
      this.player.setVolume(this.volume > 0 ? Math.max(1, Math.round(this.volume * 100)) : 0);
      // A refused autoplay is being worked around by muting; leave it muted
      // until the listener has been offered the sound back.
      if (this.blocked) return;
      if (this.muted || this.volume === 0) this.player.mute();
      else this.player.unMute();
    });
  }

  /**
   * The player is an iframe from another origin, reached through a bridge that
   * is torn down asynchronously. Every call can therefore arrive just after the
   * far end has gone, which throws; none of them are worth an error for.
   */
  safely(fn) {
    try {
      return fn();
    } catch {
      return undefined;
    }
  }

  destroy() {
    this.destroyed = true;
    clearInterval(this.ticker);
    clearTimeout(this.graceTimer);
    if (this.pendingResize) cancelAnimationFrame(this.pendingResize);
    this.sizeObserver?.disconnect();
    this.ticker = null;
    this.graceTimer = null;
    this.sizeObserver = null;
    this.pendingResize = null;
    this.target = null;
    this.safely(() => this.player?.destroy());
    this.player = null;
    this.ready = false;
  }
}

export default JukeboxPlayer;
